import React, {useCallback, useEffect, useRef, useState} from "react";
import {Box, Dialog as MuiDialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Typography} from '@mui/material';
import Button from '../../../../../../../components/base/button/button';
import Utils, {createUUId, FileReadingOptions} from "../../../../../../../../core/services/utils/utils";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import DropZone from "../../../../../../../components/base/dropzone";
// @ts-ignore
import {ReactComponent as ImagePlaceholder} from "../../../../../../../../assets/images/dropzone/upload-placeholder.svg";
import {DashboardEntry} from "../../types";

interface ImportDashboardDialogProps {
    open: boolean;
    onClose: () => void;
    onParsed: (dashboard: DashboardEntry) => void;
}

const ImportDashboardDialog = ({open, onClose, onParsed}: ImportDashboardDialogProps) => {
    const idRef = useRef(createUUId(true));
    const [parseError, setParseError] = useState<string | null>(null);
    const [parsing, setParsing] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Automatically clears the parse error message after 5 seconds to keep the UI clean and avoid confusion for the user.
     */
    useEffect(() => {
        if (!parseError)
            return;
        const timer = setTimeout(() => {
            setParseError(null);
        }, 5000)
        return () => clearTimeout(timer);
    }, [parseError])

    /**
     * Validates the structure of the provided JSON file and parses it into a DashboardEntry object.
     */
    const validateAndParseFile = useCallback(async (file: File) => {
        try {
            const json = await Utils.readFile(file, FileReadingOptions.json);
            if (!json || typeof json !== 'object') {
                return setParseError('The chosen file is not a valid JSON.');
            }
            if (!json.title) {
                return setParseError('The file must contain a "title" property.');
            }
            if (!Array.isArray(json.newWidgets)) {
                return setParseError('The file must contain an array of "newWidgets".');
            }
            return json as DashboardEntry;
        } catch (e) {
            setParseError('Error while parsing the provided JSON file. The file may be corrupted.');
        }
    }, [])

    /**
     * Handles the file selection event from the DropZone component, initiates the parsing process, and manages the loading state and error handling accordingly.
     */
    const onFileSelected = useCallback(async (files: File[]) => {
        setParseError(null);
        if (!files || files.length === 0) return;
        const file = files[0];
        if (!file) return;

        setParsing(true);
        const parsed = await validateAndParseFile(file);

        if (!isMounted())
            return;

        setParsing(false);
        if (parsed) {
            onParsed(parsed);
        }
    }, [validateAndParseFile, isMounted, onParsed])

    return (
        <MuiDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Import Dashboard</DialogTitle>
            <DialogContent dividers sx={{padding: 0, p: 2}}>
                <DropZone
                    disabled={parsing}
                    identifier={'import-json-file'}
                    accept={['.json', 'application/json'] as any}
                    id={idRef.current}
                    setLoading={() => (void 0)}
                    setTempUrl={() => (void 0)}
                    loading={false}
                    className={''}
                    dropZoneClassName={''}
                    labelClassName={''}
                    uploading={false}
                    noParse
                    onSuccess={onFileSelected}
                    error={!!parseError}
                    multiple={false}
                    openOnClick
                >
                    {() => (
                        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, minHeight: 300}}>
                            <Typography variant="body1" color="textSecondary" align="center" gutterBottom>
                                Upload your exported dashboard (.json) file here by clicking or dragging and dropping.
                            </Typography>
                            <Box sx={{opacity: 0.6, my: 4}}>
                                <ImagePlaceholder width={120} height={120}/>
                            </Box>
                            {parsing && (
                                <Box sx={{width: '100%', mt: 2}}>
                                    <LinearProgress color="primary"/>
                                    <Typography variant="body2" color="primary" align="center" sx={{mt: 1}}>
                                        Parsing provided file...
                                    </Typography>
                                </Box>
                            )}
                            {parseError && (
                                <Typography variant="body2" color="error" align="center" sx={{mt: 2, fontWeight: 600}}>
                                    {parseError}
                                </Typography>
                            )}
                        </Box>
                    )}
                </DropZone>
            </DialogContent>
            <DialogActions className="p-0">
                <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%'}}>
                    <Button
                        disabled={parsing}
                        variant="dialog"
                        color="primary"
                        onClick={onClose}
                        sx={{borderRadius: '0 10px 0 10px'}}
                    >
                        Close
                    </Button>
                </Box>
            </DialogActions>
        </MuiDialog>
    );
};

export default ImportDashboardDialog;
